import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import PageBackButton from '../../components/PageBackButton';
import { compressGaps } from '../../utils/priceUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Modal } from '../../components/ui/modal';
import {
  getSymbolDetail,
  getCompanyProfile,
  getFinancials,
  getDividends,
  getEarnings,
  getSymbolPrices,
  getSymbolChart,
} from '../../api/dataWarehouseApi';
import { fetchAccounts } from '../../api/api';
import {
  Building2, Users, DollarSign, TrendingUp, TrendingDown,
  BarChart3, Calendar, FileText, Loader2, ExternalLink, ChevronDown, ChevronUp,
  Maximize2, Minimize2,
} from 'lucide-react';
import TradingChart from '../../components/TradingChart';

const TIMEFRAME_OPTIONS = [
  { label: '1M', value: 'M1' },
  { label: '5M', value: 'M5' },
  { label: '15M', value: 'M15' },
  { label: '30M', value: 'M30' },
  { label: '1H', value: 'H1' },
  { label: '4H', value: 'H4' },
  { label: '1D', value: 'D1' },
  { label: '1W', value: 'W1' },
  { label: '1MN', value: 'MN' },
];

// ── Helpers ──────────────────────────────────────────────────────

function fmt(val, decimals = 2) {
  if (val == null) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtBig(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

function fmtPct(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function fmtDate(val) {
  if (!val) return '—';
  try { return new Date(val).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return val; }
}

function colorForChange(val) {
  if (val == null) return '';
  return Number(val) >= 0 ? 'text-success' : 'text-destructive';
}

// ── Metric Card ──────────────────────────────────────────────────

function MetricCard({ label, value, sub, icon: Icon, tone }) {
  const toneCls = tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-destructive' : 'text-primary';
  return (
    <div className="rounded-xl border border-borderDefault bg-backgroundSurface p-4 flex items-start gap-3">
      {Icon && <div className={`mt-0.5 ${toneCls}`}><Icon className="w-5 h-5" /></div>}
      <div className="min-w-0">
        <p className="text-xs text-textSecondary truncate">{label}</p>
        <p className={`text-lg font-semibold ${toneCls}`}>{value}</p>
        {sub && <p className="text-xs text-textSecondary mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ── Tab Button ───────────────────────────────────────────────────

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition whitespace-nowrap ${
        active ? 'bg-primary text-textAccent' : 'text-textSecondary hover:bg-primary/10'
      }`}
    >
      {children}
    </button>
  );
}

// ── Financial Table ──────────────────────────────────────────────

function FinancialTable({ data, type }) {
  const items = useMemo(() => {
    const filtered = (data || []).filter(r => r.statement_type === type);
    return filtered.slice(0, 20);
  }, [data, type]);

  if (!items.length) return <p className="text-textSecondary text-sm py-4">No {type} statements available.</p>;

  const cols = type === 'income'
    ? [
      { key: 'fiscal_period', label: 'Period' },
      { key: 'fiscal_year', label: 'Year' },
      { key: 'revenue', label: 'Revenue', big: true },
      { key: 'gross_profit', label: 'Gross Profit', big: true },
      { key: 'operating_income', label: 'Op. Income', big: true },
      { key: 'net_income', label: 'Net Income', big: true },
      { key: 'eps_basic', label: 'EPS', dec: 2 },
    ]
    : type === 'balance'
    ? [
      { key: 'fiscal_period', label: 'Period' },
      { key: 'fiscal_year', label: 'Year' },
      { key: 'total_assets', label: 'Total Assets', big: true },
      { key: 'total_liabilities', label: 'Liabilities', big: true },
      { key: 'total_equity', label: 'Equity', big: true },
      { key: 'cash_and_equivalents', label: 'Cash', big: true },
      { key: 'long_term_debt', label: 'LT Debt', big: true },
    ]
    : [
      { key: 'fiscal_period', label: 'Period' },
      { key: 'fiscal_year', label: 'Year' },
      { key: 'operating_cash_flow', label: 'Op. CF', big: true },
      { key: 'investing_cash_flow', label: 'Inv. CF', big: true },
      { key: 'financing_cash_flow', label: 'Fin. CF', big: true },
      { key: 'free_cash_flow', label: 'Free CF', big: true },
    ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-borderDefault">
            {cols.map(c => (
              <th key={c.key} className="text-left py-2 px-3 text-textSecondary font-medium whitespace-nowrap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <tr key={i} className="border-b border-borderDefault/50 hover:bg-primary/5 transition">
              {cols.map(c => (
                <td key={c.key} className="py-2 px-3 whitespace-nowrap">
                  {c.big ? fmtBig(row[c.key]) : c.dec != null ? fmt(row[c.key], c.dec) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Dividends Table ──────────────────────────────────────────────

function DividendsTable({ data }) {
  if (!data?.length) return <p className="text-textSecondary text-sm py-4">No dividend history available.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-borderDefault">
            <th className="text-left py-2 px-3 text-textSecondary font-medium">Ex-Date</th>
            <th className="text-left py-2 px-3 text-textSecondary font-medium">Pay Date</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">Amount</th>
            <th className="text-left py-2 px-3 text-textSecondary font-medium">Currency</th>
            <th className="text-left py-2 px-3 text-textSecondary font-medium">Frequency</th>
            <th className="text-left py-2 px-3 text-textSecondary font-medium">Type</th>
          </tr>
        </thead>
        <tbody>
          {data.slice(0, 40).map((d, i) => (
            <tr key={i} className="border-b border-borderDefault/50 hover:bg-primary/5 transition">
              <td className="py-2 px-3">{fmtDate(d.ex_dividend_date)}</td>
              <td className="py-2 px-3">{fmtDate(d.pay_date)}</td>
              <td className="py-2 px-3 text-right font-mono">${fmt(d.cash_amount, 4)}</td>
              <td className="py-2 px-3">{d.currency || 'USD'}</td>
              <td className="py-2 px-3">{d.frequency ? `${d.frequency}x/yr` : '—'}</td>
              <td className="py-2 px-3">{d.dividend_type || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Earnings Table ───────────────────────────────────────────────

function EarningsTable({ data }) {
  if (!data?.length) return <p className="text-textSecondary text-sm py-4">No earnings data available.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-borderDefault">
            <th className="text-left py-2 px-3 text-textSecondary font-medium">Period</th>
            <th className="text-left py-2 px-3 text-textSecondary font-medium">Report Date</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">EPS Actual</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">EPS Est.</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">Surprise</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">Revenue</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">Rev. Est.</th>
          </tr>
        </thead>
        <tbody>
          {data.slice(0, 20).map((e, i) => {
            const beat = e.eps_surprise != null && Number(e.eps_surprise) > 0;
            const miss = e.eps_surprise != null && Number(e.eps_surprise) < 0;
            return (
              <tr key={i} className="border-b border-borderDefault/50 hover:bg-primary/5 transition">
                <td className="py-2 px-3 font-medium">{e.fiscal_period} {e.fiscal_year}</td>
                <td className="py-2 px-3">{fmtDate(e.report_date)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmt(e.eps_actual, 2)}</td>
                <td className="py-2 px-3 text-right font-mono text-textSecondary">{fmt(e.eps_estimate, 2)}</td>
                <td className={`py-2 px-3 text-right font-mono ${beat ? 'text-success' : miss ? 'text-destructive' : ''}`}>
                  {e.eps_surprise != null ? `${Number(e.eps_surprise) > 0 ? '+' : ''}${fmt(e.eps_surprise, 2)}` : '—'}
                  {e.eps_surprise_pct != null && <span className="ml-1 text-xs">({fmtPct(e.eps_surprise_pct)})</span>}
                </td>
                <td className="py-2 px-3 text-right">{fmtBig(e.revenue_actual)}</td>
                <td className="py-2 px-3 text-right text-textSecondary">{fmtBig(e.revenue_estimate)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Price History Table ──────────────────────────────────────────

function PriceTable({ data }) {
  if (!data?.length) return <p className="text-textSecondary text-sm py-4">No price data available.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-borderDefault">
            <th className="text-left py-2 px-3 text-textSecondary font-medium">Date</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">Open</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">High</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">Low</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">Close</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">Volume</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">RSI</th>
            <th className="text-right py-2 px-3 text-textSecondary font-medium">MACD</th>
          </tr>
        </thead>
        <tbody>
          {data.map((b, i) => (
            <tr key={i} className="border-b border-borderDefault/50 hover:bg-primary/5 transition">
              <td className="py-2 px-3 whitespace-nowrap">{fmtDate(b.timestamp)}</td>
              <td className="py-2 px-3 text-right font-mono">{fmt(b.open)}</td>
              <td className="py-2 px-3 text-right font-mono">{fmt(b.high)}</td>
              <td className="py-2 px-3 text-right font-mono">{fmt(b.low)}</td>
              <td className="py-2 px-3 text-right font-mono font-semibold">{fmt(b.close)}</td>
              <td className="py-2 px-3 text-right">{b.volume?.toLocaleString() ?? '—'}</td>
              <td className="py-2 px-3 text-right">{fmt(b.rsi_14, 1)}</td>
              <td className="py-2 px-3 text-right">{fmt(b.macd_histogram, 4)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ExplorerPage — Main Component
// ══════════════════════════════════════════════════════════════════

export default function ExplorerPage() {
  const { symbol } = useParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // data
  const [sym, setSym] = useState(null);
  const [profile, setProfile] = useState(null);
  const [financials, setFinancials] = useState([]);
  const [dividends, setDividends] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [prices, setPrices] = useState([]);
  const [finSubTab, setFinSubTab] = useState('income');
  const [chartTimeframe, setChartTimeframe] = useState('D1');
  const [chartFullscreen, setChartFullscreen] = useState(false);
  const [chartPrices, setChartPrices] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartFallbackTf, setChartFallbackTf] = useState(null);
  const [studioAccounts, setStudioAccounts] = useState([]);
  const [studioAccountsLoading, setStudioAccountsLoading] = useState(false);
  const [studioAccountError, setStudioAccountError] = useState(null);
  const [studioAccountModalOpen, setStudioAccountModalOpen] = useState(false);
  const [selectedStudioAccountId, setSelectedStudioAccountId] = useState('');
  // e.g. "Requested M30 but only D1 data is available"

  const navigateToStudio = useCallback((accountId) => {
    if (!accountId || !symbol) return;
    navigate('/studio', { state: { accountId, symbol } });
  }, [navigate, symbol]);

  const closeStudioAccountModal = useCallback(() => {
    setStudioAccountModalOpen(false);
    setStudioAccountError(null);
  }, []);

  const confirmStudioAccount = useCallback(() => {
    if (!selectedStudioAccountId) return;
    closeStudioAccountModal();
    navigateToStudio(selectedStudioAccountId);
  }, [closeStudioAccountModal, navigateToStudio, selectedStudioAccountId]);

  const handleOpenInStudio = useCallback(async () => {
    if (!symbol) return;

    setStudioAccountsLoading(true);
    setStudioAccountError(null);

    try {
      const response = await fetchAccounts();
      const accounts = Array.isArray(response) ? response : response?.data;
      const normalizedAccounts = Array.isArray(accounts) ? accounts : [];
      const sortedAccounts = [...normalizedAccounts].sort((left, right) => {
        if (Boolean(left?.is_default) !== Boolean(right?.is_default)) {
          return Number(Boolean(right?.is_default)) - Number(Boolean(left?.is_default));
        }
        return String(left?.name || '').localeCompare(String(right?.name || ''));
      });

      setStudioAccounts(sortedAccounts);

      if (sortedAccounts.length === 0) {
        setSelectedStudioAccountId('');
        setStudioAccountError('No trading accounts are available for Studio.');
        setStudioAccountModalOpen(true);
        return;
      }

      if (sortedAccounts.length === 1) {
        navigateToStudio(sortedAccounts[0].id);
        return;
      }

      const preferredAccount = sortedAccounts.find((account) => account?.is_default) || sortedAccounts[0];
      setSelectedStudioAccountId(String(preferredAccount?.id ?? ''));
      setStudioAccountModalOpen(true);
    } catch (err) {
      setStudioAccounts([]);
      setSelectedStudioAccountId('');
      setStudioAccountError(err?.message || 'Failed to load trading accounts.');
      setStudioAccountModalOpen(true);
    } finally {
      setStudioAccountsLoading(false);
    }
  }, [navigateToStudio, symbol]);

  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    setLoading(true);
    setError(null);

    Promise.allSettled([
      getSymbolDetail(symbol),
      getCompanyProfile(symbol),
      getFinancials(symbol),
      getDividends(symbol),
      getEarnings(symbol),
      getSymbolPrices(symbol, { timeframe: 'D1', limit: 100, indicators: '1' }),
    ]).then(([symR, profR, finR, divR, earR, priceR]) => {
      if (!alive) return;
      if (symR.status === 'fulfilled') setSym(symR.value);
      if (profR.status === 'fulfilled') setProfile(profR.value);
      if (finR.status === 'fulfilled') {
        const arr = finR.value?.results || finR.value;
        setFinancials(Array.isArray(arr) ? arr : []);
      }
      if (divR.status === 'fulfilled') {
        const arr = divR.value?.results || divR.value;
        setDividends(Array.isArray(arr) ? arr : []);
      }
      if (earR.status === 'fulfilled') {
        const arr = earR.value?.results || earR.value;
        setEarnings(Array.isArray(arr) ? arr : []);
      }
      if (priceR.status === 'fulfilled') {
        const bars = priceR.value?.bars || priceR.value?.results || priceR.value;
        setPrices(Array.isArray(bars) ? bars : []);
      }
      if (symR.status === 'rejected') setError('Symbol not found');
      setLoading(false);
    });

    return () => { alive = false; };
  }, [symbol]);

  // Fetch chart data when chart tab is active or timeframe changes
  // Uses DW chart endpoint which serves D1+ from DB and sub-D1 from Polygon API
  useEffect(() => {
    if (!symbol || tab !== 'chart') return;
    let alive = true;
    setChartLoading(true);
    setChartFallbackTf(null);
    getSymbolChart(symbol, { timeframe: chartTimeframe, limit: 500 })
      .then((res) => {
        if (!alive) return;
        const candles = res?.candles || res?.results || res;
        setChartPrices(Array.isArray(candles) ? candles : []);
        // If backend fell back to a different timeframe, track it for the banner and effective TF
        if (res?.served_timeframe && res.served_timeframe !== chartTimeframe) {
          setChartFallbackTf(res.served_timeframe);
        } else {
          setChartFallbackTf(null);
        }
      })
      .catch(() => { if (alive) setChartPrices([]); })
      .finally(() => { if (alive) setChartLoading(false); });
    return () => { alive = false; };
  }, [symbol, chartTimeframe, tab]);

  // Convert candles to ohlcv format expected by TradingChart
  // Raw OHLCV (no compression yet) — used for timeframe detection
  const chartOhlcvRaw = useMemo(() => {
    if (!chartPrices.length) return [];
    return chartPrices
      .map((b) => ({
        time: typeof b.time === 'number' ? b.time
          : typeof b.timestamp === 'string' ? Math.floor(new Date(b.timestamp).getTime() / 1000)
          : typeof b.timestamp === 'number' ? b.timestamp : 0,
        open: Number(b.open),
        high: Number(b.high),
        low: Number(b.low),
        close: Number(b.close),
        volume: Number(b.volume || 0),
      }))
      .filter((b) => b.time > 0)
      .sort((a, b) => a.time - b.time);
  }, [chartPrices]);

  // Detect effective timeframe from actual data cadence (works on raw, uncompressed data).
  const detectedTimeframe = useMemo(() => {
    if (chartOhlcvRaw.length < 3) return chartTimeframe;
    const gaps = [];
    for (let i = 1; i < Math.min(chartOhlcvRaw.length, 50); i++) {
      gaps.push(chartOhlcvRaw[i].time - chartOhlcvRaw[i - 1].time);
    }
    gaps.sort((a, b) => a - b);
    const median = gaps[Math.floor(gaps.length / 2)];
    if (median >= 72000) return 'D1';       // ≥20h → daily
    if (median >= 18000) return 'H4';       // ≥5h  → 4H
    return chartTimeframe;
  }, [chartOhlcvRaw, chartTimeframe]);

  // Effective timeframe: prefer backend signal, then auto-detected, then user-selected
  const effectiveTimeframe = chartFallbackTf || (detectedTimeframe !== chartTimeframe ? detectedTimeframe : chartTimeframe);

  // Compressed OHLCV — uses effectiveTimeframe to decide whether/how to compress
  const chartOhlcv = useMemo(
    () => compressGaps(chartOhlcvRaw, effectiveTimeframe),
    [chartOhlcvRaw, effectiveTimeframe]
  );

  // Load more older candles when chart scrolls to left edge
  const loadMoreInFlightRef = useRef(false);
  const loadMoreHistory = useCallback(async (step = 500) => {
    if (loadMoreInFlightRef.current || !symbol) return { added: 0, total: chartPrices.length };
    const oldest = chartPrices.length > 0
      ? chartPrices.reduce((min, b) => {
          const t = typeof b.time === 'number' ? b.time : 0;
          return t > 0 && t < min ? t : min;
        }, Infinity)
      : undefined;
    if (!oldest || oldest === Infinity) return { added: 0, total: chartPrices.length };

    loadMoreInFlightRef.current = true;
    try {
      const res = await getSymbolChart(symbol, {
        timeframe: effectiveTimeframe,
        limit: step,
        before: oldest,
      });
      const raw = res?.candles || res?.results || res;
      const chunk = (Array.isArray(raw) ? raw : []).filter(
        (b) => typeof b.time === 'number' && b.time < oldest
      );
      if (chunk.length > 0) {
        setChartPrices((prev) => [...chunk, ...prev]);
        return { added: chunk.length, total: chartPrices.length + chunk.length };
      }
      return { added: 0, total: chartPrices.length };
    } catch {
      return { added: 0, total: chartPrices.length };
    } finally {
      loadMoreInFlightRef.current = false;
    }
  }, [symbol, effectiveTimeframe, chartPrices]);

  // Derived values
  const latestPrice = prices?.[0];
  const changePct = latestPrice
    ? ((Number(latestPrice.close) - Number(latestPrice.open)) / Number(latestPrice.open) * 100)
    : null;

  if (loading) {
    return (
      <PageLayout title="Symbol Explorer">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="Symbol Explorer">
        <div className="text-center py-20">
          <p className="text-destructive text-lg mb-4">{error}</p>
          <PageBackButton label="Back to Research" fallbackTo="/research" className="mx-auto" />
        </div>
      </PageLayout>
    );
  }

  return (
    <div className="my-2 sm:my-4 md:my-6 mx-auto max-w-7xl space-y-4">
      {/* ── Header ── */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Back button */}
            <PageBackButton label="Back" fallbackTo="/research" hideLabelOnMobile />

            {/* Logo & name */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {(profile?.logo_url || sym?.logo_url) && (
                <img
                  src={profile?.logo_url || sym?.logo_url}
                  alt=""
                  className="w-12 h-12 rounded-lg object-contain bg-backgroundInput border border-borderDefault"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold truncate">
                  {symbol}
                  {sym?.name && <span className="text-textSecondary font-normal ml-2 text-base">{sym.name}</span>}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {sym?.asset_class && <Badge>{sym.asset_class}</Badge>}
                  {sym?.exchange && <Badge variant="info">{sym.exchange}</Badge>}
                  {(profile?.sector || sym?.sector) && <Badge variant="info">{profile?.sector || sym?.sector}</Badge>}
                  {(profile?.industry || sym?.industry) && <span className="text-xs text-textSecondary">{profile?.industry || sym?.industry}</span>}
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse items-start gap-3 sm:flex-row sm:items-start sm:gap-4 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={handleOpenInStudio} disabled={studioAccountsLoading}>
                {studioAccountsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Open in Studio
              </Button>
              {latestPrice && (
                <div className="text-left sm:text-right">
                  <p className="text-2xl font-bold font-mono">{fmt(latestPrice.close)}</p>
                  <p className={`text-sm font-medium ${colorForChange(changePct)}`}>{fmtPct(changePct)}</p>
                  <p className="text-xs text-textSecondary">{fmtDate(latestPrice.timestamp)}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Modal
        open={studioAccountModalOpen}
        onClose={closeStudioAccountModal}
        title={`Open ${symbol} in Studio`}
        maxWidthClass="max-w-xl"
        footer={(
          <>
            <Button type="button" variant="outline" onClick={closeStudioAccountModal}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmStudioAccount} disabled={!selectedStudioAccountId || studioAccountsLoading || Boolean(studioAccountError)}>
              Continue
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <p className="text-sm text-textSecondary">
            Select the trading account that Studio should use for {symbol}.
          </p>

          {studioAccountsLoading && (
            <div className="flex items-center gap-2 rounded-lg border border-borderDefault bg-backgroundInput px-4 py-3 text-sm text-textSecondary">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              Loading accounts...
            </div>
          )}

          {!studioAccountsLoading && studioAccountError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {studioAccountError}
            </div>
          )}

          {!studioAccountsLoading && !studioAccountError && (
            <div className="space-y-2">
              {studioAccounts.map((account) => {
                const isSelected = String(selectedStudioAccountId) === String(account.id);
                return (
                  <button
                    key={account.id}
                    type="button"
                    onClick={() => setSelectedStudioAccountId(String(account.id))}
                    className={`w-full rounded-xl border px-4 py-3 text-left transition ${isSelected ? 'border-primary bg-primary/10 shadow-sm' : 'border-borderDefault bg-backgroundInput hover:border-primary/30 hover:bg-primary/5'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-textPrimary truncate">{account.name || `Account ${account.id}`}</div>
                        <div className="mt-1 flex flex-wrap gap-2 text-xs text-textSecondary">
                          {account.platform ? <span>{account.platform}</span> : null}
                          {account.broker ? <span>{account.broker}</span> : null}
                        </div>
                      </div>
                      <div className="flex flex-wrap justify-end gap-2 shrink-0">
                        {account.is_default ? <Badge>Default</Badge> : null}
                        {isSelected ? <Badge variant="info">Selected</Badge> : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Modal>

      {/* ── Tabs ── */}
      <div className="flex gap-1 flex-wrap bg-backgroundSurface rounded-xl border border-borderDefault p-1">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'financials', label: 'Financials' },
          { id: 'dividends', label: 'Dividends' },
          { id: 'earnings', label: 'Earnings' },
          { id: 'chart', label: 'Chart' },
          { id: 'prices', label: 'Price History' },
        ].map(t => (
          <TabBtn key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</TabBtn>
        ))}
      </div>

      {/* ── Tab Content ── */}

      {/* Overview */}
      {tab === 'overview' && (
        <div className="space-y-4">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <MetricCard label="Market Cap" value={fmtBig(profile?.market_cap)} icon={Building2} />
            <MetricCard label="Employees" value={profile?.employees?.toLocaleString() ?? '—'} icon={Users} />
            <MetricCard label="P/E Ratio" value={fmt(financials.find(f => f.statement_type === 'income')?.eps_basic ? (Number(latestPrice?.close) / Number(financials.find(f => f.statement_type === 'income')?.eps_basic)) : null, 1)} icon={BarChart3} />
            <MetricCard label="EPS" value={fmt(financials.find(f => f.statement_type === 'income')?.eps_basic, 2)} icon={DollarSign} />
            <MetricCard label="RSI (14)" value={fmt(latestPrice?.rsi_14, 1)} icon={TrendingUp}
              tone={latestPrice?.rsi_14 != null && Number(latestPrice.rsi_14) < 30 ? 'success' : latestPrice?.rsi_14 != null && Number(latestPrice.rsi_14) > 70 ? 'danger' : undefined} />
            <MetricCard label="ADX (14)" value={fmt(latestPrice?.adx_14, 1)} icon={TrendingUp} />
            <MetricCard label="52W High" value={fmt(prices.length ? Math.max(...prices.map(p => Number(p.high))) : null)} icon={ChevronUp} />
            <MetricCard label="52W Low" value={fmt(prices.length ? Math.min(...prices.map(p => Number(p.low))) : null)} icon={ChevronDown} />
          </div>

          {/* Company Description */}
          {profile?.description && (
            <Card>
              <CardHeader><CardTitle className="text-lg">About</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm text-textSecondary leading-relaxed">{profile.description}</p>
              </CardContent>
            </Card>
          )}

          {/* Company Details */}
          {profile && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Company Details</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  {profile.homepage_url && (
                    <div className="flex justify-between py-1 border-b border-borderDefault/50">
                      <span className="text-textSecondary">Website</span>
                      <a href={profile.homepage_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                        {new URL(profile.homepage_url).hostname} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                  {profile.primary_exchange && (
                    <div className="flex justify-between py-1 border-b border-borderDefault/50">
                      <span className="text-textSecondary">Exchange</span>
                      <span>{profile.primary_exchange}</span>
                    </div>
                  )}
                  {profile.country && (
                    <div className="flex justify-between py-1 border-b border-borderDefault/50">
                      <span className="text-textSecondary">Country</span>
                      <span>{profile.country}</span>
                    </div>
                  )}
                  {profile.currency && (
                    <div className="flex justify-between py-1 border-b border-borderDefault/50">
                      <span className="text-textSecondary">Currency</span>
                      <span>{profile.currency}</span>
                    </div>
                  )}
                  {profile.list_date && (
                    <div className="flex justify-between py-1 border-b border-borderDefault/50">
                      <span className="text-textSecondary">IPO Date</span>
                      <span>{fmtDate(profile.list_date)}</span>
                    </div>
                  )}
                  {profile.share_class_shares_outstanding && (
                    <div className="flex justify-between py-1 border-b border-borderDefault/50">
                      <span className="text-textSecondary">Shares Outstanding</span>
                      <span>{Number(profile.share_class_shares_outstanding).toLocaleString()}</span>
                    </div>
                  )}
                  {profile.sic_description && (
                    <div className="flex justify-between py-1 border-b border-borderDefault/50">
                      <span className="text-textSecondary">SIC</span>
                      <span>{profile.sic_code} — {profile.sic_description}</span>
                    </div>
                  )}
                  {profile.phone_number && (
                    <div className="flex justify-between py-1 border-b border-borderDefault/50">
                      <span className="text-textSecondary">Phone</span>
                      <span>{profile.phone_number}</span>
                    </div>
                  )}
                  {profile.address && (
                    <div className="flex justify-between py-1 border-b border-borderDefault/50">
                      <span className="text-textSecondary">Address</span>
                      <span className="text-right">{[profile.address, profile.city, profile.state, profile.postal_code].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent prices (mini table) */}
          {prices.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Recent Prices</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setTab('prices')}>View All</Button>
              </CardHeader>
              <CardContent>
                <PriceTable data={prices.slice(0, 10)} />
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Financials */}
      {tab === 'financials' && (
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex gap-1 mb-4 flex-wrap">
              {[
                { id: 'income', label: 'Income Statement' },
                { id: 'balance', label: 'Balance Sheet' },
                { id: 'cash_flow', label: 'Cash Flow' },
              ].map(t => (
                <TabBtn key={t.id} active={finSubTab === t.id} onClick={() => setFinSubTab(t.id)}>{t.label}</TabBtn>
              ))}
            </div>
            <FinancialTable data={financials} type={finSubTab} />
          </CardContent>
        </Card>
      )}

      {/* Dividends */}
      {tab === 'dividends' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Dividend History</CardTitle>
          </CardHeader>
          <CardContent>
            {dividends.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <MetricCard label="Latest Dividend" value={`$${fmt(dividends[0]?.cash_amount, 4)}`} icon={DollarSign} />
                <MetricCard label="Frequency" value={dividends[0]?.frequency ? `${dividends[0].frequency}x/yr` : '—'} icon={Calendar} />
                <MetricCard label="Total Dividends" value={dividends.length} icon={FileText} />
                <MetricCard label="Annual Total"
                  value={`$${fmt(dividends.filter(d => {
                    const y = new Date(d.ex_dividend_date).getFullYear();
                    return y === new Date().getFullYear() || y === new Date().getFullYear() - 1;
                  }).reduce((s, d) => s + Number(d.cash_amount), 0), 2)}`}
                  icon={DollarSign} sub="Current/Last Year" />
              </div>
            )}
            <DividendsTable data={dividends} />
          </CardContent>
        </Card>
      )}

      {/* Earnings */}
      {tab === 'earnings' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Earnings Reports</CardTitle>
          </CardHeader>
          <CardContent>
            {earnings.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <MetricCard label="Latest EPS" value={fmt(earnings[0]?.eps_actual, 2)} icon={DollarSign}
                  tone={earnings[0]?.eps_surprise > 0 ? 'success' : earnings[0]?.eps_surprise < 0 ? 'danger' : undefined} />
                <MetricCard label="EPS Surprise" value={earnings[0]?.eps_surprise != null ? fmtPct(earnings[0].eps_surprise_pct) : '—'} icon={TrendingUp}
                  tone={earnings[0]?.eps_surprise > 0 ? 'success' : earnings[0]?.eps_surprise < 0 ? 'danger' : undefined} />
                <MetricCard label="Beats" value={earnings.filter(e => Number(e.eps_surprise) > 0).length} icon={TrendingUp} tone="success"
                  sub={`of ${earnings.length} quarters`} />
                <MetricCard label="Misses" value={earnings.filter(e => Number(e.eps_surprise) < 0).length} icon={TrendingDown} tone="danger"
                  sub={`of ${earnings.length} quarters`} />
              </div>
            )}
            <EarningsTable data={earnings} />
          </CardContent>
        </Card>
      )}

      {/* Chart */}
      {tab === 'chart' && (
        <div className={chartFullscreen
          ? 'fixed inset-0 z-50 bg-backgroundSurface flex flex-col'
          : ''
        }>
          {chartFullscreen && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-borderDefault">
              <span className="text-sm font-semibold">{symbol}</span>
              <Button variant="ghost" size="sm" onClick={() => setChartFullscreen(false)}>
                <Minimize2 className="w-4 h-4" />
              </Button>
            </div>
          )}
          <Card className={chartFullscreen ? 'flex-1 min-h-0 rounded-none border-0' : ''}>
            <CardContent className="p-0 h-full">
              <div className={`relative flex flex-col overflow-hidden ${
                chartFullscreen ? 'h-full' : 'h-[550px]'
              }`}>
                {!chartFullscreen && (
                  <button
                    onClick={() => setChartFullscreen(true)}
                    className="absolute bottom-3 right-3 z-40 p-1.5 rounded bg-backgroundSurface/80 border border-borderDefault hover:bg-backgroundInput transition"
                    title="Fullscreen"
                  >
                    <Maximize2 className="w-4 h-4 text-textSecondary" />
                  </button>
                )}
                {chartLoading && chartOhlcv.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : chartOhlcv.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-textSecondary">
                    No price data available for this timeframe
                  </div>
                ) : (
                  <>
                    {effectiveTimeframe !== chartTimeframe && (
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 z-40 px-3 py-1.5 rounded bg-yellow-900/80 border border-yellow-600 text-yellow-200 text-xs">
                        {chartTimeframe} data unavailable — showing {effectiveTimeframe} instead
                      </div>
                    )}
                  <TradingChart
                    isBacktest
                    showControls
                    ohlcvData={chartOhlcv}
                    loadMoreHistoryProp={loadMoreHistory}
                    selectedSymbol={symbol}
                    selectedTimeframe={effectiveTimeframe}
                    showTimeframeOverlay
                    timeframeOptions={TIMEFRAME_OPTIONS}
                    onTimeframeChange={setChartTimeframe}
                  />
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Price History */}
      {tab === 'prices' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Price History (D1)</CardTitle>
          </CardHeader>
          <CardContent>
            <PriceTable data={prices} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
